/**
 * Utilitários centrais de CNPJ com suporte ao formato alfanumérico
 * (IN RFB 2.229/2024): as posições 1-12 aceitam letras (A-Z) e dígitos;
 * os 2 últimos caracteres permanecem dígitos verificadores numéricos.
 * Para CNPJs somente numéricos o cálculo do DV é idêntico ao legado.
 */

const CNPJ_CLEAN_REGEX = /[^A-Z0-9]/g;
const CNPJ_VALID_REGEX = /^[A-Z0-9]{12}[0-9]{2}$/;

const DV_WEIGHTS_1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const DV_WEIGHTS_2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

/**
 * Remove a máscara preservando letras e normaliza para maiúsculas.
 * Nunca usar .replace(/\D/g, '') em CNPJ: apaga as letras e faz
 * CNPJs diferentes colidirem nos mesmos dígitos.
 * @example cleanCNPJ('12.abc.345/01de-35') // '12ABC34501DE35'
 */
export function cleanCNPJ(value?: string | null): string {
  return (value ?? '').toUpperCase().replace(CNPJ_CLEAN_REGEX, '');
}

function calcDigit(base: string, weights: number[]): number {
  const sum = base
    .split('')
    .reduce((acc, char, i) => acc + (char.charCodeAt(0) - 48) * weights[i], 0);
  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
}

/**
 * Valida CNPJ (puro ou mascarado, case-insensitive) pelo mod-11,
 * com cada caractere valendo (código ASCII - 48): '0'-'9' valem 0-9
 * e 'A'-'Z' valem 17-42.
 */
export function isValidCNPJ(value?: string | null): boolean {
  const cnpj = cleanCNPJ(value);

  if (!CNPJ_VALID_REGEX.test(cnpj)) return false;
  if (/^([A-Z0-9])\1+$/.test(cnpj)) return false;

  return (
    calcDigit(cnpj.slice(0, 12), DV_WEIGHTS_1) === Number(cnpj[12]) &&
    calcDigit(cnpj.slice(0, 13), DV_WEIGHTS_2) === Number(cnpj[13])
  );
}

/**
 * Formata como ##.###.###/####-## quando o valor limpo tem os 14
 * caracteres esperados; caso contrário devolve o valor original.
 * @example formatCNPJ('12ABC34501DE35') // '12.ABC.345/01DE-35'
 */
export function formatCNPJ(value?: string | null): string {
  const cnpj = cleanCNPJ(value);
  if (!CNPJ_VALID_REGEX.test(cnpj)) return value ?? '';
  return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(
    5,
    8,
  )}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`;
}

/**
 * Máscara de CNPJ para react-text-mask: 12 posições alfanuméricas
 * seguidas de 2 dígitos verificadores numéricos.
 */
export const CNPJ_TEXT_MASK = [
  /[A-Za-z0-9]/,
  /[A-Za-z0-9]/,
  '.',
  /[A-Za-z0-9]/,
  /[A-Za-z0-9]/,
  /[A-Za-z0-9]/,
  '.',
  /[A-Za-z0-9]/,
  /[A-Za-z0-9]/,
  /[A-Za-z0-9]/,
  '/',
  /[A-Za-z0-9]/,
  /[A-Za-z0-9]/,
  /[A-Za-z0-9]/,
  /[A-Za-z0-9]/,
  '-',
  /[0-9]/,
  /[0-9]/,
];
